// src/pages/AdminPage.js
import React, { useState, useRef } from 'react';
import PdfRenderer from '../components/PdfRenderer';
import '../components/PdfRenderer.css';

// Configure react-pdf worker
import { pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const defaultPdf = `${process.env.PUBLIC_URL}/rotary-youth-exchange.pdf`;

// Helper to generate a unique ID
const generateUniqueId = () => `uid_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

function AdminPage() {
  const [pdfFile] = useState(defaultPdf);
  const [rectangles, setRectangles] = useState([]);
  
  // --- Refs to calculate the offset ---
  const panelRef = useRef(null); // Ref for the container div
  const pageRef = useRef(null);  // Ref for the actual PDF page, passed from renderer

  const getOffset = () => {
    if (!panelRef.current || !pageRef.current) {
      return { x: 0, y: 0 };
    }
    const panelRect = panelRef.current.getBoundingClientRect();
    const pageRect = pageRef.current.getBoundingClientRect();
    return {
      x: pageRect.left - panelRect.left,
      y: pageRect.top - panelRect.top,
    };
  };

  const updateRectangle = (uniqueId, newProps) => {
    setRectangles(prev =>
      prev.map(rect => (rect.uniqueId === uniqueId ? { ...rect, ...newProps } : rect))
    );
  };

  const deleteRectangle = (uniqueId) => {
    setRectangles(prev => prev.filter(rect => rect.uniqueId !== uniqueId));
  };

  const addRectangle = (newRect) => {
    const rectWithDefaults = {
      ...newRect,
      id: `field_${Date.now()}`, // Default user-facing ID
      type: 'text',
      uniqueId: generateUniqueId(), // Internal unique ID
    };
    setRectangles(prev => [...prev, rectWithDefaults]);
  };

  const downloadTemplate = () => {
    const offset = getOffset();

    // Before saving, SUBTRACT the offset from the coordinates
    const correctedBoxes = rectangles.map(({ uniqueId, ...rest }) => ({
      ...rest,
      x: rest.x - offset.x,
      y: rest.y - offset.y,
    }));

    const template = { boxes: correctedBoxes };
    const blob = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleTemplateUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result);
          if (data.boxes && Array.isArray(data.boxes)) {
            // We need to wait for the page to render to get the offset.
            // A simple timeout is a pragmatic way to do this.
            setTimeout(() => {
              const offset = getOffset();
              
              // After loading, ADD the offset to the coordinates
              const offsetBoxes = data.boxes.map(box => ({
                ...box,
                x: box.x + offset.x,
                y: box.y + offset.y,
                uniqueId: generateUniqueId(),
              }));
              setRectangles(offsetBoxes);
            }, 200); // 200ms delay to allow the page to render and refs to be set
          } else {
            alert('Invalid template file: "boxes" array not found.');
          }
        } catch (error) {
          alert('Error parsing JSON file.');
        }
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className="container">
      <div className="header">
        <h1>Admin: PDF Template Designer</h1>
        <p>Using default PDF: <strong>{defaultPdf.replace(process.env.PUBLIC_URL, '')}</strong></p>
      </div>
      <div className="page-content">
        <div className="admin-layout">
          <div className="pdf-panel">
            <h2>Draw & Configure Boxes</h2>
            <PdfRenderer
              ref={panelRef} // Move the ref to the component that does the drawing
              pdfFile={pdfFile}
              rectangles={rectangles}
              onAddRectangle={addRectangle}
              onUpdateRectangle={updateRectangle}
              deleteRectangle={deleteRectangle}
              pageRef={pageRef} // Pass the ref for the actual page down
            />
          </div>
          <div className="config-panel">
            <h2>Configuration</h2>
            <input
              type="file"
              accept=".json"
              onChange={handleTemplateUpload}
              style={{ display: 'block', marginBottom: '10px' }}
            />
            <button onClick={downloadTemplate} disabled={rectangles.length === 0}>
              Save Template
            </button>
            <hr />
            <div className="rect-list">
              {rectangles.map((rect, index) => (
                <div key={rect.uniqueId} className="rect-config">
                  <strong>Box {index + 1}</strong> (Page {rect.page})
                  <div>
                    <label>ID:</label>
                    <input
                      type="text"
                      value={rect.id}
                      onChange={(e) => updateRectangle(rect.uniqueId, { id: e.target.value })}
                    />
                  </div>
                  <div>
                    <label>Type:</label>
                    <select
                      value={rect.type}
                      onChange={(e) => updateRectangle(rect.uniqueId, { type: e.target.value })}
                    >
                      <option value="text">Text</option>
                      <option value="image">Image</option>
                      <option value="signature">Signature</option>
                    </select>
                  </div>
                  {rect.type === 'text' && (
                    <div>
                      <label>Default Value:</label>
                      <input
                        type="text"
                        value={rect.defaultValue || ''}
                        onChange={(e) => updateRectangle(rect.uniqueId, { defaultValue: e.target.value })}
                        placeholder="Optional default text"
                      />
                    </div>
                  )}
                  <button className="delete-btn" onClick={() => deleteRectangle(rect.uniqueId)}>Delete</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminPage;
